import {
  getGetReelEditQueryKey,
  getListApprovalsQueryKey,
  useGetReelEdit,
  useGetReelEditVersion,
  useRestoreReelEdit,
  useRestoreReelOriginal,
  useSaveReelEdit,
} from '@reelplicate/api-client/approvals';
import type { ReelEditDto, ReelEditStateDto } from '@reelplicate/api-client/model';
import {
  DEFAULT_CAPTION_STYLE,
  presetById,
  type ReelTimeline,
  reelTimelineSchema,
} from '@reelplicate/flows';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef } from 'react';

import { initialState, timelineReducer } from './timeline-state';

/** How often a render is checked on. */
const POLL_MS = 1500;

const isOpen = (edit: ReelEditDto | undefined) =>
  edit?.status === 'QUEUED' || edit?.status === 'RUNNING';

/** A timeline as text with its keys in schema order, to compare two by content. */
const canonical = (timeline: ReelTimeline) => {
  const parsed = reelTimelineSchema.safeParse(timeline);
  return JSON.stringify(parsed.success ? parsed.data : timeline);
};

/** The timeline a saved state opens with, defaults filled so it compares cleanly. */
const parseTimeline = (state: ReelEditStateDto): ReelTimeline | null => {
  const parsed = reelTimelineSchema.safeParse(state.timeline);
  return parsed.success ? parsed.data : null;
};

/**
 * Review's reel editor for one waiting reel: the saved state from the API,
 * the timeline being edited (with undo), and Save → render → swap. While a
 * version renders it's polled; when it's done the draft and the queue
 * refresh to show it.
 */
export function useReelEditor({
  workspaceId,
  approvalId,
  enabled,
}: {
  workspaceId: string;
  approvalId: string;
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const stateQuery = useGetReelEdit(workspaceId, approvalId, {
    query: { enabled: enabled && Boolean(workspaceId), throwOnError: false },
  });
  const saved = stateQuery.data;
  const savedTimeline = useMemo(() => (saved ? parseTimeline(saved) : null), [saved]);

  const [state, dispatch] = useReducer(timelineReducer, undefined, () =>
    initialState(
      { version: 1, clips: [{ id: 'clip-1', in: 0, out: 1, volume: 1 }], texts: [] },
      1,
    ),
  );

  // Open on what's saved. Later the editor keeps its own edit - through a
  // render, or a failed one - until a restore or Discard replaces it.
  const loaded = useRef(false);
  useEffect(() => {
    if (!saved || !savedTimeline || loaded.current) return;
    loaded.current = true;
    dispatch({
      type: 'reset',
      timeline: savedTimeline,
      sourceDuration: saved.sourceDurationSeconds,
    });
  }, [saved, savedTimeline]);

  const latest = saved?.versions[0];
  const pendingId = isOpen(latest) ? latest!.id : undefined;
  const pending = useGetReelEditVersion(workspaceId, approvalId, pendingId ?? '', {
    query: {
      enabled: Boolean(pendingId),
      refetchInterval: (query) => (isOpen(query.state.data) ? POLL_MS : false),
      throwOnError: false,
    },
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetReelEditQueryKey(workspaceId, approvalId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListApprovalsQueryKey(workspaceId, { status: 'PENDING' }),
      }),
    ]);
  };

  // A render finished: show it (or say why it didn't).
  const finished = pending.data && !isOpen(pending.data) ? pending.data : undefined;
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!finished || announced.current === finished.id) return;
    announced.current = finished.id;
    if (finished.status === 'FAILED') {
      toast.error(finished.error ?? 'The edit couldn’t be rendered.');
    } else {
      toast.success(`Version ${finished.version} is ready.`);
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const save = useSaveReelEdit();
  const restoreVersion = useRestoreReelEdit();
  const restoreOriginal = useRestoreReelOriginal();

  const rendering = Boolean(pendingId) && !finished;
  // Changed since the last save: against the version rendering, else the live one.
  const baseline = useMemo(() => {
    const parsed =
      rendering && latest ? reelTimelineSchema.safeParse(latest.timeline) : null;
    return parsed?.success ? parsed.data : savedTimeline;
  }, [rendering, latest, savedTimeline]);
  const dirty = useMemo(
    () => baseline !== null && canonical(state.timeline) !== canonical(baseline),
    [state.timeline, baseline],
  );

  // What the API would refuse, said before saving.
  const problem = useMemo(() => {
    const parsed = reelTimelineSchema.safeParse(state.timeline);
    if (parsed.success) return null;
    const issue = parsed.error.issues[0];
    return issue?.path[0] === 'texts' && issue.code === 'too_small'
      ? 'Text can’t be empty.'
      : (issue?.message ?? null);
  }, [state.timeline]);

  const show = (next: ReelEditStateDto) => {
    queryClient.setQueryData(getGetReelEditQueryKey(workspaceId, approvalId), next);
    const timeline = parseTimeline(next);
    if (timeline)
      dispatch({ type: 'reset', timeline, sourceDuration: next.sourceDurationSeconds });
    void queryClient.invalidateQueries({
      queryKey: getListApprovalsQueryKey(workspaceId, { status: 'PENDING' }),
    });
  };

  return {
    saved,
    loading: stateQuery.isPending && enabled,
    error: stateQuery.error,
    state,
    dispatch,
    dirty,
    problem,
    rendering,
    /** The version rendering now, if any. */
    pendingVersion: rendering ? (pending.data ?? latest) : undefined,
    saving: save.isPending,
    restoring: restoreVersion.isPending || restoreOriginal.isPending,
    async save() {
      try {
        await save.mutateAsync({
          id: workspaceId,
          approvalId,
          data: {
            timeline: state.timeline,
            ...(latest ? { baseEditId: latest.id } : {}),
          },
        });
        await queryClient.invalidateQueries({
          queryKey: getGetReelEditQueryKey(workspaceId, approvalId),
        });
      } catch {
        // Shown as a toast.
      }
    },
    /** Text at `at`, in the default caption look. */
    addText(at: number) {
      dispatch({ type: 'addText', at, style: presetById(DEFAULT_CAPTION_STYLE)!.style });
    },
    /** Back to what's saved. */
    discard() {
      if (saved && baseline)
        dispatch({
          type: 'reset',
          timeline: baseline,
          sourceDuration: saved.sourceDurationSeconds,
        });
    },
    async restore(editId: string | null) {
      try {
        show(
          editId
            ? await restoreVersion.mutateAsync({ id: workspaceId, approvalId, editId })
            : await restoreOriginal.mutateAsync({ id: workspaceId, approvalId }),
        );
      } catch {
        // Shown as a toast.
      }
    },
  };
}

export type ReelEditor = ReturnType<typeof useReelEditor>;
